import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useId,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  addBetSlipMultipleEvents,
  betslipIsEmpty,
  createBetslipEventsFromSwarm,
  setBookBetIdInLs
} from 'utils/betslip/betslip';
import { SYSTEM_BET_MIN_EVENTS_COUNT } from 'utils/constants/app/magic-numbers-app';
import {
  BET_BUILDER,
  BET_TYPES,
  BETSLIP_BET_TYPES_BY_BET_NUMBER_TYPE
} from 'utils/constants/betslip/bet-types';
import { showToastError } from 'utils/generic/show-toast-error';
import {
  BetSlipEventObj,
  BookBetParamsType,
  BookingIdObject,
  BookingIds
} from 'interfaces/bet-data';
import {
  EBetslipBetType,
  IEventsAndGameIds,
  ResponseBookingSelections,
  SelectionsFromBetslip
} from 'interfaces/betslip';
import { ResponseGeneric } from 'interfaces/generic';
import { SwarmDataBetslipEvents } from 'interfaces/sportsbook-data-levels';
import {
  bookBet,
  getEventsByBookingId,
  getEventsReadyForBetslip
} from 'services/bet-slip';
import { dispatchBetslipBetType } from 'newelements/_Betslip/utils/action-middlewares/store-actions-middlewares';
import { addQuery } from 'components/QueryRoute';
import { useBetslipNotEmptyPopup } from 'hooks/betslip/useBetlsipNotEmptyPopup';
import { setAfterSignInActions } from 'store/actions';
import {
  setIsBookingMode,
  setMustOpen,
  setSystemMinCombinationsCount
} from 'store/actions/betslip';
import { getAfterSignInActions } from 'store/selectors/app-data';
import {
  getAllLsBets,
  getAllStakes,
  getSelectedSystem
} from 'store/selectors/betslip';
import { getBetslip } from 'store/selectors/spec-elems';
import { getIsLoggedIn } from 'store/selectors/user-data';

type Props = {
  setBookingModalVisible: (
    value: ((prevState: boolean) => boolean) | boolean
  ) => void;
  setBookIds: Dispatch<SetStateAction<BookingIds>>;
  currentType: EBetslipBetType | string;
  totalStake: number;
  setSystemMinCount: (
    value: ((prevState: number | null) => number | null) | number | null
  ) => void;
  betslipRef: { current: HTMLElement | null | undefined };
  betslipUid?: string;
};

type BookBetReturn = {
  bookId: string;
  setBookId: (value: ((prevState: string) => string) | string) => void;
  bookingProgress: boolean;
  getByBookIdProgress: boolean;
  onBookBet: () => void;
  onSubmitBookId: () => void;
  getEvents: () => void;
};

let afterSignInFetcherId = '';

export const useBookBet = ({
  setBookingModalVisible,
  setBookIds,
  currentType,
  totalStake,
  betslipRef,
  betslipUid
}: Props): BookBetReturn => {
  const hookId = useId();

  const dispatch = useDispatch();
  const stakes = useSelector(getAllStakes);
  const afterSignInActions = useSelector(getAfterSignInActions);
  const { showPopup: showBetslipNotEmptyPopup } = useBetslipNotEmptyPopup();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const allLsBets = useSelector(getAllLsBets);
  // const system = useSelector(getSystem);
  const system = useSelector(getSelectedSystem);
  const betslipPresent = useSelector(getBetslip);
  const { t } = useTranslation();
  const history = useHistory();
  const [bookId, setBookId] = useState<string>('');
  const [bookingProgress, setBookingProgress] = useState<boolean>(false);
  const [getByBookIdProgress, setGetByBookIdProgress] =
    useState<boolean>(false);

  const onBookBetCallback = (bookIds: BookingIds) => {
    setBookIds(bookIds);
    setBookingProgress(false);
    setBookingModalVisible(true);
  };

  const onBookBetCallbackError = () => {
    setBookIds([]);
    setBookingProgress(false);
    setBookingModalVisible(false);
  };

  const onBookBet = useCallback(() => {
    const getBets = (betType: number) => {
      if (betType === BET_TYPES.SINGLE) {
        return allLsBets.map(({ eventId, coeficient }) => ({
          Amount: Number(stakes.single[eventId]) || 0,
          BetType: betType,
          Selections: [
            {
              SelectionId: eventId,
              Price: Number(coeficient)
            }
          ]
        }));
      } else if (betType === BET_BUILDER.TYPE) {
        return [
          {
            BetSlipUid: betslipUid,
            Amount: Number(totalStake) || 0,
            BetType: BET_BUILDER.TYPE
          }
        ];
      } else {
        return allLsBets.reduce(
          (result: BookBetParamsType, { eventId, coeficient }) => {
            if (!result.length) {
              result.push({
                Amount: Number(stakes.general || 0),
                BetType: betType,
                Selections: [],
                ...(betType !== BET_TYPES.SYSTEM
                  ? {}
                  : { SystemMinCount: system?.value })
              });
            }

            result[0].Selections &&
              result[0].Selections.push({
                SelectionId: eventId,
                Price: Number(coeficient)
              });

            return result;
          },
          []
        );
      }
    };

    const promises: Promise<BookingIdObject>[] = [];

    setBookingProgress(true);

    switch (currentType) {
      case EBetslipBetType.SINGLE:
        getBets(BET_TYPES.SINGLE).forEach(bet => {
          promises.push(
            new Promise((resolve: (value: BookingIdObject) => void) => {
              bookBet(
                [bet],
                (data: ResponseGeneric) => {
                  resolve({
                    booking_id: data.details.Id,
                    selection_id:
                      bet.Selections && bet.Selections[0].SelectionId
                  });
                },
                onBookBetCallbackError
              );
            })
          );
        });
        Promise.all(promises).then(bookIds => {
          onBookBetCallback(bookIds);
        });
        break;
      case EBetslipBetType.CHAIN:
      case EBetslipBetType.MULTIPLE:
        {
          const betTypeKey =
            currentType === EBetslipBetType.CHAIN
              ? 'CHAIN'
              : ('MULTIPLE' as keyof typeof BET_TYPES);

          bookBet(
            getBets(BET_TYPES[betTypeKey]),
            (data: ResponseGeneric) => {
              onBookBetCallback([{ booking_id: data.details.Id }]);
            },
            onBookBetCallbackError
          );
        }

        break;

      case EBetslipBetType.SYSTEM:
        bookBet(
          getBets(BET_TYPES.SYSTEM),
          (data: ResponseGeneric) => {
            onBookBetCallback([{ booking_id: data.details.Id }]);
          },
          onBookBetCallbackError
        );
        break;
      case BET_BUILDER.NAME:
        bookBet(
          getBets(BET_BUILDER.TYPE),
          (data: ResponseGeneric) => {
            onBookBetCallback([{ booking_id: data.details.Id }]);
          },
          onBookBetCallbackError
        );
        break;
      default:
        break;
    }
  }, [allLsBets, currentType, stakes, totalStake, system, betslipUid]);

  useEffect(() => {
    if (!afterSignInFetcherId) {
      afterSignInFetcherId = hookId;
    }

    if (
      afterSignInActions.bookBetId &&
      isLoggedIn &&
      !window.mustRefreshForSessionVisibility &&
      afterSignInFetcherId === hookId
    ) {
      betslipPresent && dispatch(setMustOpen(true));
      getEvents(afterSignInActions.bookBetId);
      const afterSignInActionsCopy = Object.assign({}, afterSignInActions);
      delete afterSignInActionsCopy.bookBetId;
      dispatch(setAfterSignInActions(afterSignInActionsCopy));
    }
  }, [isLoggedIn, afterSignInActions.bookBetId]);

  const onSubmitBookId = useCallback(() => {
    if (!isLoggedIn) {
      history.push(addQuery({ accounts: '*', login: '*' }));

      dispatch(
        setAfterSignInActions({
          ...afterSignInActions,
          bookBetId: bookId
        })
      );
    } else {
      getEvents();
    }
  }, [bookId, isLoggedIn, afterSignInActions]);

  const getEvents = (id: string | null = null) => {
    setGetByBookIdProgress(true);
    getEventsByBookingId(
      id || bookId,
      (data: ResponseBookingSelections) => {
        setGetByBookIdProgress(false);

        if (
          typeof data.details === 'string' ||
          !Object.keys(data.details).length
        ) {
          showToastError(t('betslip.wrongBookId'), betslipRef.current);

          return;
        }

        const eventsAndGames: IEventsAndGameIds[] =
          data.details.bets[0].selections?.map(
            ({ match_id, selection_id }) => ({
              gameId: match_id,
              eventId: selection_id
            })
          );

        getEventsReadyForBetslip(
          eventsAndGames,
          (betslipEventsData: { data: SwarmDataBetslipEvents }) => {
            if (!Object.keys(betslipEventsData.data.sport).length) {
              showToastError(t('betslip.bookIdExpired'), betslipRef.current);

              return;
            }

            if (betslipIsEmpty()) {
              afterSubmitBookId(
                data,
                betslipEventsData.data,
                eventsAndGames.length
              );
            } else {
              showBetslipNotEmptyPopup(() => {
                afterSubmitBookId(
                  data,
                  betslipEventsData.data,
                  eventsAndGames.length
                );
              });
            }
          }
        );
      },
      (errorData: any) => {
        showToastError(errorData.data || errorData.msg, betslipRef.current);
        setGetByBookIdProgress(false);
      }
    );
  };

  const afterSubmitBookId = (
    bookingData: ResponseBookingSelections,
    betslipEventsData: SwarmDataBetslipEvents,
    originalEventsLength: number
  ) => {
    const betslipReadyEvents = createBetslipEventsFromSwarm(betslipEventsData);

    if (
      bookingData.details.match_ids &&
      bookingData.details.bets[0].bet_type === EBetslipBetType.CHAIN
    ) {
      const betslipEventsToSort = betslipReadyEvents.splice(
        0,
        betslipReadyEvents.length
      );

      bookingData.details.match_ids.forEach(matchId => {
        const event = betslipEventsToSort.find(
          ({ gameId }) => gameId === matchId
        ) as BetSlipEventObj;

        event && betslipReadyEvents.push(event);
      });
    }

    const {
      bets: [{ system_min_count: selectedSystem }],
      bets: [{ bet_type }]
    } = bookingData.details as SelectionsFromBetslip;

    addBetSlipMultipleEvents(betslipReadyEvents, true);

    if (
      betslipReadyEvents.length === bookingData.details.match_ids.length &&
      selectedSystem
    ) {
      // @Todo maybe need to rename
      dispatch(setSystemMinCombinationsCount(selectedSystem));
    }

    if (
      !(
        (bet_type === EBetslipBetType.SYSTEM &&
          betslipReadyEvents.length < SYSTEM_BET_MIN_EVENTS_COUNT) ||
        ([EBetslipBetType.MULTIPLE, EBetslipBetType.CHAIN].includes(bet_type) &&
          betslipReadyEvents.length < 2)
      )
    ) {
      dispatchBetslipBetType(
        BETSLIP_BET_TYPES_BY_BET_NUMBER_TYPE[bet_type as unknown as number]
      );
    }

    dispatch(setIsBookingMode(false));

    if (originalEventsLength === betslipReadyEvents.length) {
      setBookBetIdInLs(bookId);
    }

    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has('booking_id')) {
      queryParams.delete('booking_id');
      history.replace({
        search: queryParams.toString()
      });
    }
  };

  return {
    bookId,
    setBookId,
    bookingProgress,
    getByBookIdProgress,
    onBookBet,
    onSubmitBookId,
    getEvents
  };
};
